import { Alert, Box, Stack, Tab, Tabs } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid-premium";
import { Dialog } from "components/Dialog";
import { useState } from "react";

import { ContextAnalysisTabPaper } from "./ContextAnalysisTabBody";
import { ContextUsageSummary } from "./ContextUsageSummary";
import { prettyPartType } from "./scoutContextAnalysisFormat";
import {
  type ContextSummary,
  type PartRow,
  type SubTab,
  useScoutConversationContextAnalysis,
} from "./useScoutConversationContextAnalysis";

interface Props {
  conversationId: string;
}

export function ScoutConversationContextAnalysis({ conversationId }: Props) {
  return <ContextAnalysisInner key={conversationId} conversationId={conversationId} />;
}

function ContextAnalysisInner({ conversationId }: Props) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [documentPaginationModel, setDocumentPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [activePart, setActivePart] = useState<PartRow | null>(null);
  const [subTab, setSubTab] = useState<SubTab>("roundtrips");
  const analysis = useScoutConversationContextAnalysis({
    conversationId,
    subTab,
    paginationModel,
    documentPaginationModel,
  });

  return (
    <Stack spacing={1} sx={{ width: "100%", height: "100%", minHeight: 0, overflow: "hidden" }}>
      {analysis.error ? (
        <Alert severity="error">Failed to load context analysis: {analysis.error.message}</Alert>
      ) : null}
      <ContextUsageSummary summary={analysis.summary} />
      <ContextAnalysisTabs summary={analysis.summary} subTab={subTab} onChange={setSubTab} />
      <ContextAnalysisTabPaper
        subTab={subTab}
        analysis={analysis}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        documentPaginationModel={documentPaginationModel}
        onDocumentPaginationModelChange={setDocumentPaginationModel}
        onPartClick={setActivePart}
      />
      <PartDetailDialog part={activePart} onClose={() => setActivePart(null)} />
    </Stack>
  );
}

function ContextAnalysisTabs({
  summary,
  subTab,
  onChange,
}: {
  summary: ContextSummary | undefined;
  subTab: SubTab;
  onChange: (tab: SubTab) => void;
}) {
  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
      <Tabs
        value={subTab}
        onChange={(_, v: SubTab) => onChange(v)}
        sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40 } }}
      >
        <Tab value="roundtrips" label={countTabLabel("Per-roundtrip", summary?.roundtripCount)} />
        <Tab value="turns" label={countTabLabel("Per-turn rollup", summary?.turnCount)} />
        <Tab value="context" label={countTabLabel("Turn context", summary?.turnCount)} />
        <Tab value="parts" label={countTabLabel("Message parts", summary?.partCount)} />
        <Tab
          value="documents"
          label={countTabLabel(
            "Large doc extraction",
            summary?.documentAttachmentCount ? summary.documentAttachmentCount : undefined
          )}
        />
        <Tab value="system" label={systemPromptTabLabel(summary?.latestPrelude)} />
        <Tab value="tools" label={toolDefinitionsTabLabel(summary?.latestPrelude)} />
      </Tabs>
    </Box>
  );
}

function countTabLabel(base: string, count: number | undefined): string {
  if (count == null) {
    return base;
  }
  return `${base} (${count})`;
}

function systemPromptTabLabel(prelude: ContextSummary["latestPrelude"] | undefined): string {
  if (prelude?.turnNumber == null) {
    return "System prompt";
  }
  if (!prelude.systemPromptTokens) {
    return `System prompt · turn ${prelude.turnNumber}`;
  }
  return `System prompt · turn ${prelude.turnNumber} · ${prelude.systemPromptTokens.toLocaleString()}`;
}

function toolDefinitionsTabLabel(prelude: ContextSummary["latestPrelude"] | undefined): string {
  if (!prelude?.toolDefinitionsTokens) {
    return "Tool definitions";
  }
  return `Tool definitions · ${prelude.toolDefinitionsTokens.toLocaleString()}`;
}

function PartDetailDialog({ part, onClose }: { part: PartRow | null; onClose: () => void }) {
  return (
    <Dialog
      open={Boolean(part)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      title={part ? `Part #${part.seq} — ${prettyPartType(part.type)}` : ""}
      noPrimaryAction
      hideCancelButton
      DialogContentProps={{ dividers: true }}
    >
      {part ? (
        <Box
          component="pre"
          sx={{
            margin: 0,
            fontFamily: "monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            backgroundColor: "action.hover",
            p: 1.5,
            borderRadius: 1,
          }}
        >
          {JSON.stringify(part, null, 2)}
        </Box>
      ) : null}
    </Dialog>
  );
}
